import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import { useWsStatus, WsState } from "../stores/ws-status.js";

export function useNetworks() {
  return useQuery({ queryKey: ["networks"], queryFn: api.getNetworks });
}

export function useNetworkStats(networkId: string) {
  const wsState = useWsStatus((s) => s.state);
  return useQuery({
    queryKey: ["networks", networkId, "stats"],
    queryFn: () => api.getNetworkStats(networkId),
    enabled: !!networkId,
    refetchInterval: wsState !== WsState.Disconnected ? false :10_000,
  });
}

export const TX_PAGE_LIMIT = 25;

export function useTxs(networkId: string, page = 1, filters?: Record<string, string>) {
  return useQuery({
    queryKey: ["networks", networkId, "txs", page, filters],
    queryFn: () => api.getTxs(networkId, page, TX_PAGE_LIMIT, filters),
    enabled: !!networkId,
    placeholderData: (prev) => prev,
  });
}

/**
 * Network-wide distributions behind the filter knobs. Keyed OUTSIDE the
 * ["networks", id, "txs"] prefix on purpose: the websocket invalidates that
 * prefix on every new tx, and the knobs should be a stable map of the
 * network, not something that redraws mid-brush.
 */
export function useTxFacets(networkId: string) {
  return useQuery({
    queryKey: ["networks", networkId, "tx-facets"],
    queryFn: () => api.getTxFacets(networkId),
    enabled: !!networkId,
    staleTime: 5 * 60_000,
  });
}

/**
 * Facet counts under the active filters (each dimension excluding its own
 * range, server-side). Overlaid on the network-wide backdrop from
 * useTxFacets, so it only runs while filters are active. placeholderData
 * keeps the previous overlay up during the refetch — no flicker per change.
 */
export function useTxFilteredFacets(networkId: string, filters: Record<string, string>, enabled: boolean) {
  return useQuery({
    queryKey: ["networks", networkId, "tx-facets-filtered", filters],
    queryFn: () => api.getTxFacets(networkId, filters),
    enabled: !!networkId && enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/** Aggregate stats over the filtered set — inside the "txs" prefix so it live-updates. */
export function useTxStats(networkId: string, filters?: Record<string, string>) {
  return useQuery({
    queryKey: ["networks", networkId, "txs", "filter-stats", filters],
    queryFn: () => api.getTxStats(networkId, filters),
    enabled: !!networkId,
    placeholderData: (prev) => prev,
  });
}

/**
 * Privacy-set breakdown and fee sample for a saved view. Kept outside the
 * "txs" prefix like the facets: it joins cluster memberships and ships up to
 * a few thousand fee points, too heavy to redo on every websocket tx — and
 * the cluster side only moves once per analysis run anyway.
 */
export function useTxInsights(networkId: string, filters: Record<string, string>) {
  return useQuery({
    queryKey: ["networks", networkId, "tx-insights", filters],
    queryFn: () => api.getTxInsights(networkId, filters),
    enabled: !!networkId,
    staleTime: 60_000,
  });
}

export function useTxDetail(networkId: string, hash: string) {
  return useQuery({
    queryKey: ["networks", networkId, "txs", hash],
    queryFn: () => api.getTxDetail(networkId, hash),
    enabled: !!networkId && !!hash,
  });
}

export function useClusterRuns(networkId: string) {
  return useQuery({
    queryKey: ["networks", networkId, "clusters"],
    queryFn: () => api.getClusterRuns(networkId),
    enabled: !!networkId,
  });
}

export function useClusterDetail(networkId: string, runId: number) {
  return useQuery({
    queryKey: ["networks", networkId, "clusters", runId],
    queryFn: () => api.getClusterDetail(networkId, runId),
    enabled: !!networkId && runId > 0,
  });
}

export function useUmapData(networkId: string, runId: number) {
  return useQuery({
    queryKey: ["networks", networkId, "clusters", runId, "umap"],
    queryFn: () => api.getUmapData(networkId, runId),
    enabled: !!networkId && runId > 0,
  });
}

export function useOutliers(networkId: string, runId: number, limit = 50) {
  return useQuery({
    queryKey: ["networks", networkId, "clusters", runId, "outliers", limit],
    queryFn: () => api.getOutliers(networkId, runId, limit),
    enabled: !!networkId && runId > 0,
  });
}

export function useLabels(networkId: string) {
  return useQuery({
    queryKey: ["networks", networkId, "labels"],
    queryFn: () => api.getLabels(networkId),
    enabled: !!networkId,
  });
}

export function useAddLabel(networkId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { address: string; label: string; contractType?: string }) =>
      api.addLabel(networkId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["networks", networkId, "labels"] }),
  });
}

export function useDeleteLabel(networkId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (labelId: number) => api.deleteLabel(networkId, labelId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["networks", networkId, "labels"] }),
  });
}

export function useClusterMembers(networkId: string, runId: number, clusterId: number | null) {
  return useQuery({
    queryKey: ["networks", networkId, "clusters", runId, "members", clusterId],
    queryFn: () => api.getClusterMembers(networkId, runId, clusterId!),
    enabled: !!networkId && runId > 0 && clusterId !== null,
  });
}

export function useTxSlotActivity(networkId: string, hash: string) {
  return useQuery({
    queryKey: ["networks", networkId, "txs", hash, "slot-activity"],
    queryFn: () => api.getTxSlotActivity(networkId, hash),
    enabled: !!networkId && !!hash,
  });
}

export function useFeePayerStats(networkId: string) {
  return useQuery({
    queryKey: ["networks", networkId, "fee-payer-stats"],
    queryFn: () => api.getFeePayerStats(networkId),
    enabled: !!networkId,
  });
}

export function useMurderBoard(networkId: string, address: string, page = 1, limit = 50, clusterId?: number | null) {
  return useQuery({
    queryKey: ["networks", networkId, "murder-board", address, page, limit, clusterId],
    queryFn: () => api.getMurderBoard(networkId, address, { page, limit, clusterId }),
    enabled: !!networkId && !!address,
    placeholderData: (prev) => prev,
  });
}

export function useAddressInteractions(networkId: string, address: string) {
  return useQuery({
    queryKey: ["networks", networkId, "murder-board", address, "address-interactions"],
    queryFn: () => api.getAddressInteractions(networkId, address),
    enabled: !!networkId && !!address,
  });
}

export function useRepeatedSlots(networkId: string, address: string) {
  return useQuery({
    queryKey: ["networks", networkId, "murder-board", address, "repeated-slots"],
    queryFn: () => api.getRepeatedSlots(networkId, address),
    enabled: !!networkId && !!address,
  });
}

export function useCoTxs(networkId: string, address: string, coAddress: string, page = 1, enabled = false) {
  return useQuery({
    queryKey: ["networks", networkId, "murder-board", address, "co", coAddress, page],
    queryFn: () => api.getCoTxs(networkId, address, coAddress, { page, limit: 25 }),
    enabled: enabled && !!networkId && !!address && !!coAddress,
    placeholderData: (prev) => prev,
  });
}

export function useFeatureStats(networkId: string) {
  return useQuery({
    queryKey: ["networks", networkId, "feature-stats"],
    queryFn: () => api.getFeatureStats(networkId),
    enabled: !!networkId,
    staleTime: 60_000,
  });
}

export function useAnalysisStatus(networkId: string) {
  const wsState = useWsStatus((s) => s.state);
  return useQuery({
    queryKey: ["networks", networkId, "analyze-status"],
    queryFn: () => api.getAnalysisStatus(networkId),
    enabled: !!networkId,
    refetchInterval: wsState !== WsState.Disconnected ? false :5_000,
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: (password: string) => api.login(password),
  });
}

export function useTriggerAnalysis(networkId: string) {
  return useMutation({
    mutationFn: (params: {
      minClusterSize?: number;
      nNeighbors?: number;
      minDist?: number;
    }) => api.triggerAnalysis(networkId, params),
  });
}

export function useSaveAnalysisConfig(networkId: string) {
  return useMutation({
    mutationFn: (params: {
      minClusterSize?: number;
      nNeighbors?: number;
      minDist?: number;
    }) => api.saveAnalysisConfig(networkId, params),
  });
}

export function useRevertAnalysisConfig(networkId: string) {
  return useMutation({
    mutationFn: () => api.revertAnalysisConfig(networkId),
  });
}

export function useFeeHistory(
  networkId: string,
  opts?: { from?: number; to?: number; resolution?: string },
) {
  return useQuery({
    queryKey: ["networks", networkId, "fees", "history", opts],
    queryFn: () => api.getFeeHistory(networkId, opts),
    enabled: !!networkId,
  });
}

export function useFeeSpread(
  networkId: string,
  opts?: { from?: number; to?: number; bucketSize?: number },
) {
  return useQuery({
    queryKey: ["networks", networkId, "fees", "spread", opts],
    queryFn: () => api.getFeeSpread(networkId, opts),
    enabled: !!networkId,
  });
}

export function useCurrentFees(networkId: string) {
  return useQuery({
    queryKey: ["networks", networkId, "fees", "current"],
    queryFn: () => api.getCurrentFees(networkId),
    enabled: !!networkId,
  });
}

export function useManaStats(
  networkId: string,
  opts?: { blocks?: number; from?: number; to?: number },
) {
  return useQuery({
    queryKey: ["networks", networkId, "mana", "stats", opts?.blocks, opts?.from, opts?.to],
    queryFn: () => api.getManaStats(networkId, opts),
    enabled: !!networkId,
  });
}

export function useBlockHistory(
  networkId: string,
  opts?: { from?: number; to?: number; limit?: number },
) {
  return useQuery({
    queryKey: ["networks", networkId, "blocks", "history", opts],
    queryFn: () => api.getBlockHistory(networkId, opts),
    enabled: !!networkId,
  });
}

export function useBlockStats(
  networkId: string,
  opts?: { from?: number; to?: number },
) {
  return useQuery({
    queryKey: ["networks", networkId, "blocks", "stats", opts],
    queryFn: () => api.getBlockStats(networkId, opts),
    enabled: !!networkId,
  });
}

export function useBlockConfig(networkId: string) {
  return useQuery({
    queryKey: ["networks", networkId, "blocks", "config"],
    queryFn: () => api.getBlockConfig(networkId),
    enabled: !!networkId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Recorded rollup prunes overlapping a slot window.
 *
 * Long staleTime: a prune is a rare historical fact that never changes once recorded, and a new one
 * arrives with a block-stream event rather than on a timer. Refetching it on the blocks page's
 * cadence would be pure waste.
 */
export function useReorgs(networkId: string, opts?: { fromSlot?: number; toSlot?: number }) {
  return useQuery({
    queryKey: ["networks", networkId, "reorgs", opts?.fromSlot ?? null, opts?.toSlot ?? null],
    queryFn: () => api.getReorgs(networkId, opts),
    enabled: !!networkId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCheckpointStats(networkId: string, opts?: { fromBlock?: number; toBlock?: number }) {
  return useQuery({
    queryKey: ["networks", networkId, "checkpoints", "stats", opts],
    queryFn: () => api.getCheckpointStats(networkId, opts),
    enabled: !!networkId,
  });
}

export function useBlockDetail(networkId: string, blockNumber: number) {
  return useQuery({
    queryKey: ["networks", networkId, "blocks", blockNumber],
    queryFn: () => api.getBlockDetail(networkId, blockNumber),
    enabled: !!networkId && blockNumber > 0,
  });
}

export function useTxComparison(networkId: string, txHashes: string[]) {
  return useQuery({
    queryKey: ["networks", networkId, "txs", "compare", txHashes.sort().join(",")],
    queryFn: () => api.compareTxs(networkId, txHashes),
    enabled: !!networkId && txHashes.length >= 2,
  });
}

/**
 * Universal search over one network.
 *
 * `enabled` is the caller's debounce gate: the box only fires once typing has settled, so the
 * hook must not run on every keystroke's intermediate value. Results are keyed by the query
 * itself, which makes backspacing to an earlier prefix an instant cache hit rather than a
 * second round trip. What a hash resolves to barely moves, so a 30s staleTime is generous.
 */
export function useSearch(networkId: string, q: string, enabled = true, limit?: number) {
  return useQuery({
    // The limit is part of the key: the dropdown's ten-row answer and the results page's
    // hundred-row one are different responses to the same query, and serving one for the
    // other would silently truncate the page.
    queryKey: ["networks", networkId, "search", q, limit],
    queryFn: () => api.getSearch(networkId, q, limit),
    enabled: enabled && !!networkId && q.trim().length > 0,
    staleTime: 30_000,
  });
}

export function useStorageOverview() {
  return useQuery({
    queryKey: ["admin", "storage"],
    queryFn: api.getStorageOverview,
    staleTime: 60_000,
  });
}

/**
 * Prefix matching every live production-stats window for a network, and no pinned one.
 * Exported so the socket invalidates exactly what the hook keys — the two used to spell
 * the prefix out separately, which is the kind of thing that silently drifts apart.
 */
export function liveProductionStatsKey(networkId: string) {
  return ["networks", networkId, "production-stats", true];
}

/**
 * Block, checkpoint & slot production with miss attribution over one window.
 *
 * A window is live when it reaches the head, which is not the same as the caller having
 * named no selector: the dashboard's epoch strip asks for an explicit range ending at the
 * current epoch's last slot, and that end keeps moving as the epoch fills. Callers in that
 * position pass `live`. A window that really is fixed is cached indefinitely and never
 * invalidated, so stepping back to an older epoch still pins what is on screen.
 */
export function useProductionStats(
  networkId: string,
  opts: {
    hours?: number;
    epoch?: number;
    from?: number;
    to?: number;
    include?: string[];
    groupBy?: "auto" | "hour" | "day" | "epoch";
    enabled?: boolean;
    /**
     * Whether this window tracks the chain head. Defaults to "the caller named no
     * selector at all", which is only a proxy: the dashboard's recent strip asks
     * for an explicit from/to range that ends at the *current* epoch's last slot,
     * so it is as live as a trailing window and has to say so.
     */
    live?: boolean;
  },
) {
  const wsState = useWsStatus((s) => s.state);
  const isLive =
    opts.live ?? (opts.epoch == null && opts.from == null && opts.to == null);
  const include = opts.include ?? [];
  return useQuery({
    // Liveness rides in the key so the socket can invalidate exactly the windows a
    // new block changes, and leave pinned history — which will never differ — alone.
    queryKey: [
      "networks",
      networkId,
      "production-stats",
      isLive,
      opts.epoch ?? null,
      opts.from ?? null,
      opts.to ?? null,
      opts.hours ?? null,
      opts.groupBy ?? null,
      include.join(","),
    ],
    queryFn: () =>
      api.getProductionStats(networkId, {
        hours: opts.hours,
        epoch: opts.epoch,
        from: opts.from,
        to: opts.to,
        include,
        groupBy: opts.groupBy,
      }),
    enabled: !!networkId && opts.enabled !== false,
    // While the socket is up, new_blocks drives the refresh; polling is the fallback
    // for when it is not.
    refetchInterval: isLive && wsState === WsState.Disconnected ? 30_000 : false,
    staleTime: isLive ? 0 : Infinity,
  });
}

export function useChainHealth(networkId: string, epoch?: number) {
  const wsState = useWsStatus((s) => s.state);
  const isCurrentEpoch = epoch == null;
  return useQuery({
    queryKey: ["networks", networkId, "health", epoch ?? "current"],
    queryFn: () => api.getChainHealth(networkId, epoch),
    enabled: !!networkId,
    refetchInterval: isCurrentEpoch && wsState !== WsState.Disconnected ? false : isCurrentEpoch ? 10_000 : false,
    staleTime: isCurrentEpoch ? 0 : Infinity,
  });
}
